import { useEffect, useRef, useState } from 'react';
import { BedCat, type ManifestClip } from '../../../mascot/app/renderer/bed.js';
import { t } from '../lib/i18n';

/** The bed's size on screen (px), and the cat in it: its standing height, and where its paws rest (share of the bed's width / height). */
const BED_W = 96;
const CAT_H = 58;
const SEAT = { x: 0.5, y: 0.6 };
/** The canvas the cat is drawn on: wider and taller than the bed, so ears and tail aren't cut. */
const CANVAS = { w: 140, h: 120 };
const BED_H = BED_W * (285 / 320);
/** How far the mouse moves with the button down on the cat before that is picking it up (not a click). */
const PICK_MOVE = 6;

/** Window events: the cat picked up out of its bed (detail: where the mouse is, page px), or dropped into it (WindowCat). */
export const CAT_PICKUP = 'momomail:cat-pickup';
export const CAT_HOME = 'momomail:cat-home';

/** The bed's anchor: where the cat's paws rest in it, in page px (the bed holds the cat there, and a cat picked up hangs from there). */
export function bedAnchor(): { x: number; y: number } | null {
  const r = document.querySelector('.cat-bed')?.getBoundingClientRect();
  if (!r || !r.width) return null;
  return { x: r.left + r.width * SEAT.x, y: r.bottom - BED_H * (1 - SEAT.y) };
}

/** The bed on screen (page px), a little bigger: a cat let go over it goes in. */
export function overBed(x: number, y: number): boolean {
  const r = document.querySelector('.cat-bed')?.getBoundingClientRect();
  return !!r && x >= r.left - 12 && x <= r.right + 12 && y >= r.top - 24 && y <= r.bottom + 6;
}

let manifest: Promise<{ catHeight: number; clips: ManifestClip[] } | null> | null = null;
const loadManifest = () =>
  (manifest ??= fetch('cat/mascot.json')
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null));

/**
 * 馍馍's bed in the corner of the app (where its chat opens). The cat is at home in it — on the web, and in the desktop app while the
 * desktop cat is off — doing what cats do in place; otherwise the bed is empty (the cat is out). On the desktop the bed holds the cat:
 * dropped in it, it stays (CAT_HOME); picked up out of it with the mouse, it is out again (CAT_PICKUP: WindowCat takes it, held).
 */
export function CatBed({ home, open, onClick, canPickUp = false }: { home: boolean; open: boolean; onClick: () => void; canPickUp?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  // Ahead of the settings catching up: just dropped in (true, sitting) or just picked up (false)
  const [moved, setMoved] = useState<{ home: boolean; pose: 'sit' | 'curl' } | null>(null);
  const [overCat, setOverCat] = useState(false);
  const pressed = useRef<{ x: number; y: number } | null>(null);
  const swallowClick = useRef(false);
  const shown = moved?.home ?? home;

  useEffect(() => setMoved(null), [home]);
  useEffect(() => {
    const dropped = () => setMoved({ home: true, pose: 'sit' });
    window.addEventListener(CAT_HOME, dropped);
    return () => window.removeEventListener(CAT_HOME, dropped);
  }, []);

  useEffect(() => {
    if (!shown) return;
    let stop = false;
    let raf = 0;
    void loadManifest().then((m) => {
      const el = canvas.current;
      if (stop || !m || !el) return;
      const g = el.getContext('2d', { willReadFrequently: true })!;
      const dpr = window.devicePixelRatio || 1;
      el.width = CANVAS.w * dpr;
      el.height = CANVAS.h * dpr;
      const images = new Map<string, HTMLImageElement>();
      const sheet = (c: ManifestClip) => {
        let img = images.get(c.name);
        if (!img) images.set(c.name, (img = Object.assign(new Image(), { src: `cat/${c.sheet}` })));
        return img.complete && img.naturalWidth ? img : null;
      };
      for (const c of m.clips) sheet(c); // all of them up front: there are only a few
      const cat = new BedCat(m.clips, moved?.pose ?? (Math.random() < 0.6 ? 'curl' : 'sit'));
      const s = CAT_H / m.catHeight;
      // The anchor on the canvas (the canvas is centered on the bed, its bottom a little below the bed's)
      const px = CANVAS.w / 2 + (SEAT.x - 0.5) * BED_W;
      const py = CANVAS.h - (BED_H * (1 - SEAT.y) + 6);
      let last = performance.now();
      const frame = (now: number) => {
        if (stop) return;
        cat.tick(Math.min(0.1, (now - last) / 1000));
        last = now;
        const { clip, frame: k } = cat.view();
        const img = sheet(clip);
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.clearRect(0, 0, CANVAS.w, CANVAS.h);
        if (img) {
          const { w, h, cols } = clip.cell;
          g.drawImage(img, (k % cols) * w, Math.floor(k / cols) * h, w, h, px - clip.origin.x * s, py - clip.origin.y * s, w * s, h * s);
        }
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    });
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
    };
    // (moved.pose only matters when it comes home: that changes `shown`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  /** Whether the mouse is on the cat's own pixels (not the bed around it). */
  const onCat = (x: number, y: number) => {
    const el = canvas.current;
    if (!shown || !el) return false;
    const r = el.getBoundingClientRect();
    if (x < r.left || y < r.top || x >= r.right || y >= r.bottom) return false;
    const k = el.width / r.width;
    return el.getContext('2d', { willReadFrequently: true })!.getImageData(Math.floor((x - r.left) * k), Math.floor((y - r.top) * k), 1, 1).data[3]! > 40;
  };

  // Picking it up: the button down on the cat, then a move (a press and let go without moving is still a click: the chat)
  useEffect(() => {
    if (!canPickUp || !shown) return;
    const move = (e: MouseEvent) => {
      const p = pressed.current;
      if (!p) return;
      if (!(e.buttons & 1)) return void (pressed.current = null);
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < PICK_MOVE) return;
      pressed.current = null;
      swallowClick.current = true;
      setOverCat(false);
      setMoved({ home: false, pose: 'sit' });
      window.dispatchEvent(new CustomEvent(CAT_PICKUP, { detail: { x: e.clientX, y: e.clientY } }));
    };
    const up = () => void (pressed.current = null);
    window.addEventListener('mousemove', move, true);
    window.addEventListener('mouseup', up, true);
    return () => {
      window.removeEventListener('mousemove', move, true);
      window.removeEventListener('mouseup', up, true);
    };
  }, [canPickUp, shown]);

  return (
    <button
      className={`cat-bed ${open ? 'is-open' : ''} ${shown ? 'is-home' : ''} ${overCat ? 'is-over-cat' : ''}`}
      title={t('和馍馍聊聊')}
      aria-label={t('和馍馍聊聊')}
      aria-expanded={open}
      onMouseMove={(e) => canPickUp && setOverCat(onCat(e.clientX, e.clientY))}
      onMouseLeave={() => setOverCat(false)}
      onMouseDown={(e) => {
        swallowClick.current = false; // (the last pick-up ended away from the bed: no click came to swallow)
        if (canPickUp && e.button === 0 && onCat(e.clientX, e.clientY)) pressed.current = { x: e.clientX, y: e.clientY };
      }}
      onClick={() => {
        if (swallowClick.current) return void (swallowClick.current = false);
        onClick();
      }}
    >
      <img className="cat-bed__bed" src="cat/bed.webp" alt="" draggable={false} />
      {shown && <canvas ref={canvas} className="cat-bed__cat" />}
      {/* The bed's front rim again, over the cat: it sits in the bed, not on it */}
      {shown && <img className="cat-bed__front" src="cat/bed.webp" alt="" draggable={false} />}
    </button>
  );
}
